import { Div, DivProps, List, ListItem, P } from '@kizi/core-components';
import { IconV2, withTooltip } from '@kizi/shared-components';
import React, { useMemo } from 'react';
import { Field } from '..';

const TooltipIcon = withTooltip(IconV2);

const ListCompItem = ({
  data,
  leftFieldStyle,
  rightFieldStyle,
}: ListCompItemProps) => {
  const { bold, icon, label, noRightField = false, value, valueIcon } = data;

  const fontWeight = bold ? 'bold' : '';

  const leftIconSize = {
    height: 19,
    width: 19,
  };
  const rightIconSize = {
    height: 28,
    width: 28,
  };

  const isValueString = typeof value === 'string';

  const textExtraStyle: DivProps = icon || valueIcon ? { my: 'auto' } : {};

  const _tooltip = useMemo(() => {
    return data?.tooltip ? (
      <Div width="200px" variant="primaryContainer">
        {data?.tooltip}
      </Div>
    ) : null;
  }, [data?.tooltip]);

  const RightDiv = (props: DivProps) => {
    return (
      <Div
        fontWeight={fontWeight}
        ml={2}
        textAlign="right"
        width="100%"
        {...rightFieldStyle}
        {...props}
        {...textExtraStyle}
      />
    );
  };

  let rightDivComp = noRightField ? <></> : <RightDiv>{value}</RightDiv>;

  if (value) {
    if (isValueString) {
      rightDivComp = (
        <RightDiv
          dangerouslySetInnerHTML={{
            __html: String(value),
          }}
        />
      );
    }

    if (valueIcon && typeof value === 'string') {
      rightDivComp = (
        <Div {...rightIconSize}>
          <IconV2 name={value} {...rightIconSize} />
        </Div>
      );
    }
  }

  return (
    <ListItem alignItems="center">
      {icon && (
        <Div mr={2} {...leftIconSize}>
          <IconV2 fill="main.1" name={icon} {...leftIconSize} />
        </Div>
      )}
      {typeof label === 'string' ? (
        <Div
          display="flex"
          alignItems="center"
          fontWeight={fontWeight}
          mr={2}
          width="100%"
          {...leftFieldStyle}
          {...textExtraStyle}
        >
          {label}
          {data?.tooltip && (
            <TooltipIcon
              ml={2}
              position="relative"
              bottom="1px"
              name="info"
              size={18}
              css={{ fill: '#BEC0C3' }}
              _tooltip={_tooltip}
              cursor="pointer"
              _popTo="top-start"
            />
          )}
        </Div>
      ) : (
        label
      )}
      {data?.component && data?.component}
      {rightDivComp}
    </ListItem>
  );
};

export const ListComp = ({
  groups,
  listVariant = 'accordionProductInner',
  innerListVariant = 'accordionProductListPrice',
  listItemTitleVariant = 'bodyImportant',
  ...restProps
}: ListCompProps) => {
  return (
    <Div {...restProps}>
      <List variant={listVariant}>
        {groups.map(
          (
            {
              children,
              disclaimer,
              leftFieldStyle,
              title,
              rightFieldStyle,
              list,
            }: ListGroup,
            i: number,
          ) => {
            return (
              <ListItem key={`list-comp-${title}-${i}`}>
                {title && (
                  <Div variant="detailsListHeading">
                    <P variant={listItemTitleVariant}>{title}</P>
                  </Div>
                )}
                <List variant={innerListVariant}>
                  {list
                    ? list.map((item: Field, j: number) => (
                        <ListCompItem
                          data={item}
                          key={`detail-${j}`}
                          leftFieldStyle={leftFieldStyle}
                          rightFieldStyle={rightFieldStyle}
                        />
                      ))
                    : children}
                </List>
                {disclaimer && (
                  <Div color="text" fontSize={0} fontWeight={0} mt={[3, 6]}>
                    {disclaimer}
                  </Div>
                )}
              </ListItem>
            );
          },
        )}
      </List>
    </Div>
  );
};

interface ListCompProps extends DivProps {
  groups: ListGroup[];
  innerListVariant?: string;
  listItemTitleVariant?: string;
  listVariant?: string;
}

export interface ListGroup {
  children?: React.ReactChild;
  disclaimer?: string;
  leftFieldStyle?: DivProps;
  list?: Field[];
  rightFieldStyle?: DivProps;
  title: string;
}

interface ListCompItemProps
  extends Omit<ListGroup, 'children' | 'disclaimer' | 'list' | 'title'> {
  data: Field;
}
